/**
 * EngineersView.jsx - the Engineers tab: the roster as cards, and the same
 * roster as a calendar.
 *
 * This tab owns the roster. Every engineer in the database is a card here and
 * a row in the calendar - there is no hidden "internal" list filtering the
 * view any more, and naming someone on a project no longer creates or edits
 * anyone. The two views share one store (useManpower), so putting an engineer
 * on a project in the calendar changes their card and the other way round.
 *
 * Detail lives in components/engineers/.
 *
 * 10/12/26: a fifth, Staff cost rates (components/engineers/CostRatesPanel) -
 * pay, burden multiplier and burden rate per person - for the 'cost-rates'
 * area only (View reads, Edit changes).
 *
 * r53: a fourth view, Quote Rates (components/engineers/QuoteRatesPanel) -
 * the quote builder's rate card and parts catalog - for anyone who can also
 * see Quotes & Rates; only Quotes & Rates Edit can change it.
 *
 * 10/16/26 (Sharif): the CARDS show only internal engineers and interns;
 * contractors, MENA and South Africa are on Resources, which shows everyone
 * (data/resourceGroups.js). The calendar still draws everyone, in tables
 * people can now add, rename, reorder, delete and move engineers between
 * (components/manpower/useCalendarTables).
 *
 * Deep links (the Activity log's lines open them, 10/8/26):
 * #/engineers?engineer=<id> opens that engineer, #/engineers?view=calendar
 * (or cards / resources / rates) picks the view - on load and on every hash
 * change.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Pill } from '../components/ui/Pill';
import { Icon } from '../components/ui/Icon';
import { Modal } from '../components/ui/Modal';
import { Toast } from '../components/ui/Toast';
import EngineerForm from '../components/forms/EngineerForm';
import { ManpowerCalendar } from '../components/ManpowerCalendar';
import { useApp } from '../context/AppContext';
import { canEdit, canDelete, hasArea } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { exportCsv } from '../utils/exportCsv';
import { headerBg } from '../components/engineers/engineerStyles';
import { useManpower } from '../components/manpower/useManpower';
import { useCalendarTables } from '../components/manpower/useCalendarTables';
import { showsOnCards } from '../data/resourceGroups';
import EngineerDetailPanel from '../components/engineers/EngineerDetailPanel';
import ResourcesTable from '../components/engineers/ResourcesTable';
import { disciplineLabel } from '../data/disciplines';
import QuoteRatesPanel from '../components/engineers/QuoteRatesPanel';
import CostRatesPanel from '../components/engineers/CostRatesPanel';
import { parseHashFilters } from '../utils/hashParams';
import EngineerPhoto from '../components/engineers/EngineerPhoto';
import { loadEngineerPhoto } from '../hooks/useEngineerPhoto';
import { PHOTO_BIG_BYTES, shrinkImage } from '../utils/shrinkImage';

const VIEW_MODES = ['cards', 'calendar', 'resources', 'rates', 'costs'];

export default function EngineersView({ onNavigate }) {
  const { allEngineers, allProjects, deleteEngineer, updateEngineer, refresh } = useApp();
  const access = useMyAccess();
  const editable = canEdit(access, 'engineers');
  const deletable = canDelete(access, 'engineers');
  // the rates are for Quotes Edit only (Sharif, 10/6/26)
  const seesRates = hasArea(access, 'quotes', 'edit');
  // pay and burden: the 'cost-rates' area only (10/12/26)
  const seesCosts = hasArea(access, 'cost-rates', 'view');
  const editsCosts = hasArea(access, 'cost-rates', 'edit');
  const [selectedId, setSelectedId] = useState(() => parseHashFilters().engineer || null);
  const [showForm, setShowForm] = useState(false);
  const [editEngineer, setEditEngineer] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [toast, setToast] = useState(null);
  // 'cards' = the engineer card grid, 'calendar' = the same people as a calendar
  // (its Availability button shows who is free), 'resources' = current / next job,
  // 'rates' = the quote rate card (Quotes & Rates View)
  const [viewMode, setViewMode] = useState(() => (VIEW_MODES.includes(parseHashFilters().view) ? parseHashFilters().view : 'cards'));
  useEffect(() => {
    const onHash = () => {
      const h = parseHashFilters();
      if (VIEW_MODES.includes(h.view)) {
        setViewMode(h.view);
        setSelectedId(null);
      }
      if (h.engineer) setSelectedId(h.engineer);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, []);

  // The schedule, shared by both views on this tab. Cards read today's cell
  // out of it; the calendar reads and writes the whole thing.
  //
  // A calendar save also writes today's Status / Location / Project # onto
  // the engineer cards (server side, r49). The PATCH says how many cards it
  // changed; only then is the roster re-read, so the cards show it without a
  // page reload and an ordinary save costs no extra fetch.
  const onCalendarSaved = useCallback(
    (res) => {
      if (res && res.cards > 0 && refresh) refresh();
    },
    [refresh]
  );
  const mp = useManpower({ onSaved: onCalendarSaved });
  // The calendar's tables (which grids, who is in each, which count toward
  // utilization) - read here so switching views does not re-read them.
  const calendarTables = useCalendarTables();

  // The roster is simply what is in the engineers table, minus anyone
  // archived. It used to be filtered down to the names highlighted green in
  // the Manpower Planning workbook, which is why the tab and the database
  // disagreed about who exists.
  const roster = useMemo(() => (allEngineers || []).filter((e) => !(e.isArchived || e.is_archived)), [allEngineers]);

  // The cards: internal engineers and interns only. Everyone else is on
  // Resources (and the calendar); nobody is removed from the database.
  const cardRoster = useMemo(() => roster.filter(showsOnCards), [roster]);

  const selected = selectedId ? allEngineers.find((e) => e.id === selectedId) : null;
  const filtered = cardRoster.filter((e) => {
    if (statusFilter !== 'All' && e.status !== statusFilter) return false;
    return true;
  });

  // Photos stored before uploads were shrunk (10/13/26) are ~1.4 MB each. They
  // are only fetched when shown and then cached, but one click makes each a
  // 400 px JPEG, re-saved through the same PUT as the Edit form. Done here in
  // the browser because the server has no image library (and needs none).
  const bigPhotos = roster.filter((e) => (Number(e.photoBytes ?? e.photo_bytes) || 0) > PHOTO_BIG_BYTES);
  const [shrinking, setShrinking] = useState(false);
  const shrinkPhotos = async () => {
    if (shrinking || !updateEngineer) return;
    setShrinking(true);
    let done = 0;
    try {
      for (const e of bigPhotos) {
        try {
          const url = await loadEngineerPhoto(e.id, e.photoVersion || e.photo_version);
          if (!url) continue;
          const small = await shrinkImage(await (await fetch(url)).blob());
          await updateEngineer(e.id, { photoUrl: small });
          done += 1;
        } catch {
          /* that one stays as it is */
        }
      }
    } finally {
      setShrinking(false);
    }
    setToast(done ? `Shrank ${done} photo${done === 1 ? '' : 's'}` : 'No photo could be shrunk');
  };

  const handleDelete = (eng) => {
    if (!window.confirm(`Delete "${eng.name}"? This cannot be undone.`)) return;
    deleteEngineer(eng.id);
    if (selectedId === eng.id) setSelectedId(null);
    setToast('Engineer deleted');
  };

  return (
    <div>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {showForm && (
        <Modal title="Add Engineer" onClose={() => setShowForm(false)} width={660}>
          <EngineerForm onClose={() => setShowForm(false)} />
        </Modal>
      )}
      {editEngineer && (
        <Modal title="Edit Engineer" onClose={() => setEditEngineer(null)} width={660}>
          <EngineerForm onClose={() => setEditEngineer(null)} initialData={editEngineer} />
        </Modal>
      )}

      {selected ? (
        <EngineerDetailPanel
          engineer={selected}
          mp={mp}
          onBack={() => setSelectedId(null)}
          onEdit={(e) => setEditEngineer(e)}
          onDelete={handleDelete}
          onNavigate={onNavigate}
          onToast={setToast}
          editable={editable}
          deletable={deletable}
        />
      ) : (
        <>
          {/* SUMMARY ROW */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Engineers', value: cardRoster.length, color: '#0D0D0D', bg: '#fff' },
              { label: 'On-Site', value: cardRoster.filter((e) => e.status === 'On-Site').length, color: '#1B5E20', bg: '#E8F5E9' },
              { label: 'In Office', value: cardRoster.filter((e) => e.status === 'Office').length, color: '#283593', bg: '#E8EAF6' },
              { label: 'PTO / Out', value: cardRoster.filter((e) => e.status === 'PTO').length, color: '#616161', bg: '#F5F5F5' },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  background: s.bg,
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid #ECECEC',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
              >
                <span style={{ fontSize: 9.5, fontWeight: 700, color: s.color, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  {s.label}
                </span>
                <span style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</span>
              </div>
            ))}
          </div>

          {/* TOOLBAR */}
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}
          >
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['All', 'On-Site', 'Office', 'PTO'].map((s) => {
                const count = s === 'All' ? cardRoster.length : cardRoster.filter((e) => e.status === s).length;
                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 20,
                      fontSize: 10.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: 'Montserrat',
                      border: `1px solid ${statusFilter === s ? '#1B5E20' : '#E8E8E8'}`,
                      background: statusFilter === s ? '#1B5E20' : '#fff',
                      color: statusFilter === s ? '#fff' : '#666',
                    }}
                  >
                    {s} ({count})
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* View-mode toggle: switches the page between the Cards grid and the inline calendar. */}
              <div style={{ display: 'flex', background: '#F4F6F0', borderRadius: 8, padding: 3, gap: 2 }}>
                {[
                  { id: 'cards', label: 'Cards', icon: 'engineers' },
                  { id: 'calendar', label: 'Calendar', icon: 'calendar' },
                  { id: 'resources', label: 'Resources', icon: 'user' },
                  ...(seesRates ? [{ id: 'rates', label: 'Quote Rates', icon: 'dollar' }] : []),
                  ...(seesCosts ? [{ id: 'costs', label: 'Staff cost rates', icon: 'dollar' }] : []),
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setViewMode(v.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: viewMode === v.id ? '#1B5E20' : 'transparent',
                      color: viewMode === v.id ? '#fff' : '#444',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: 'Montserrat',
                    }}
                  >
                    <Icon name={v.icon} size={12} color={viewMode === v.id ? '#fff' : '#1B5E20'} /> {v.label}
                  </button>
                ))}
              </div>
              {editable && bigPhotos.length > 0 && (
                <button
                  onClick={shrinkPhotos}
                  disabled={shrinking}
                  title="Make the large engineer photos 400 px JPEGs - the cards load faster"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '1px solid #E8E8E8',
                    background: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#666',
                    cursor: shrinking ? 'progress' : 'pointer',
                    fontFamily: 'Montserrat',
                  }}
                >
                  {shrinking ? 'Shrinking photos…' : `Shrink ${bigPhotos.length} large photo${bigPhotos.length === 1 ? '' : 's'}`}
                </button>
              )}
              {editable && (
                <button
                  onClick={() => setShowForm(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                    boxShadow: '0 2px 8px rgba(27,94,32,0.25)',
                  }}
                >
                  <Icon name="plus" size={12} color="#fff" /> Add Engineer
                </button>
              )}
            </div>
          </div>

          {viewMode === 'calendar' && (
            <ManpowerCalendar engineers={roster} mp={mp} projects={allProjects} tables={calendarTables} editable={editable} />
          )}

          {viewMode === 'rates' && seesRates && <QuoteRatesPanel editable />}

          {viewMode === 'costs' && seesCosts && <CostRatesPanel editable={editsCosts} />}

          {/* RESOURCES — every region and contractors too, with the current and
          next assignment read straight off the calendar above. */}
          {viewMode === 'resources' && <ResourcesTable engineers={roster} edits={mp.edits} onOpen={(e) => setSelectedId(e.id)} />}

          {/* ENGINEER CARDS */}
          {viewMode === 'cards' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
              {filtered.map((e) => (
                <div
                  key={e.id}
                  onClick={() => setSelectedId(e.id)}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    border: '1px solid #ECECEC',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    position: 'relative',
                  }}
                  onMouseEnter={(ev) => (ev.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)')}
                  onMouseLeave={(ev) => (ev.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)')}
                >
                  {/* CARD HEADER */}
                  <div style={{ background: headerBg(e.status), padding: '18px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                      <EngineerPhoto
                        engineer={e}
                        alt={e.name}
                        fallback={
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              background: 'rgba(255,255,255,0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontSize: 14,
                              fontWeight: 800,
                            }}
                          >
                            {e.initials ||
                              (e.name || '')
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)}
                          </div>
                        }
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{e.name}</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{e.title}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Pill label={e.status} />
                    </div>
                  </div>

                  {/* CARD BODY */}
                  <div style={{ padding: '14px 16px' }}>
                    {[
                      ['Skill', e.skill],
                      ['Discipline', disciplineLabel(e.discipline)],
                      // Straight off the calendar - today's cell for this engineer.
                      // It used to be a free-text field on the row plus a fuzzy
                      // first-name match against project descriptions, so a card
                      // could claim a project the schedule knew nothing about.
                      ['Today', mp.projectOn(e.name) || '-'],
                      ['Location', e.location],
                      // Optional on the card, and written by the calendar from the
                      // block they are on today.
                      ['Project #', e.globalProjectNumber || '-'],
                    ].map(([k, v]) => (
                      <div
                        key={k}
                        style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F4F4F4' }}
                      >
                        <span style={{ fontSize: 10.5, color: '#999', fontWeight: 600 }}>{k}</span>
                        <span style={{ fontSize: 10.5, color: '#0D0D0D', fontWeight: 700, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* CARD ACTIONS */}
                  {(editable || deletable) && (
                    <div style={{ padding: '8px 16px 12px', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {editable && (
                        <button
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setEditEngineer(e);
                          }}
                          title="Edit"
                          style={{
                            background: '#fff',
                            border: '1px solid #E0E0E0',
                            borderRadius: 6,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#555',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          <Icon name="edit" size={11} color="#555" /> Edit
                        </button>
                      )}
                      {deletable && (
                        <button
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleDelete(e);
                          }}
                          title="Delete"
                          style={{
                            background: '#fff',
                            border: '1px solid #FFCDD2',
                            borderRadius: 6,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#B71C1C',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          <Icon name="trash" size={11} color="#B71C1C" /> Delete
                        </button>
                      )}
                    </div>
                  )}

                  {e.isUserCreated && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#43A047',
                        border: '2px solid rgba(255,255,255,0.5)',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {viewMode === 'cards' && roster.length > cardRoster.length && (
            <div style={{ textAlign: 'right', marginTop: 10, fontSize: 10.5, color: '#888' }}>
              Internal engineers and interns. Contractors, South Africa, MENA and Europe ({roster.length - cardRoster.length}) are on{' '}
              <button
                type="button"
                onClick={() => setViewMode('resources')}
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: '#1B5E20',
                  fontWeight: 700,
                  fontSize: 10.5,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                  textDecoration: 'underline',
                }}
              >
                Resources
              </button>
              .
            </div>
          )}

          {viewMode === 'cards' && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 11 }}>No engineers match this filter</div>
          )}

          {viewMode === 'cards' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() =>
                  exportCsv(
                    'engineers.csv',
                    roster.map((e) => ({
                      Name: e.name,
                      Title: e.title,
                      Skill: e.skill,
                      Discipline: disciplineLabel(e.discipline),
                      Today: mp.projectOn(e.name) || '',
                      Location: e.location,
                      'Global Project Number': e.globalProjectNumber || '',
                      Status: e.status,
                    }))
                  )
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #E8E8E8',
                  background: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#666',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                <Icon name="download" size={13} color="#666" /> Export Engineers
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
